export const componentLabels = [
  "Bill Acceptor",
  "Ticket Printer",
  "Cabinet",
  "Monitor",
  "Additional Hardware",
  "Installation",
] as const;
export type ComponentName = (typeof componentLabels)[number];
export type MachineProfile = {
  base: string[];
  options: Partial<Record<ComponentName, string[]>>;
};
// Publish only merchant-confirmed standards and compatible options, keyed by catalogue machine ID.
const standardBase = [
  "Wiring harness", "Edge connectors", "Power supply", "Switches", "Cooling fan",
  "Meters", "PCB mounting feet", "Reset switches",
];
function machineProfile(game: string): MachineProfile {
  return {
    base: [...standardBase, `${game} gaming board`],
    options: {
      "Bill Acceptor": ["TOPS Bill Acceptor", "Pyramid 7600 Bill Acceptor", "Pyramid Platinum"],
      "Ticket Printer": ["TOPS Printer", "Pyramid Printer"],
      Cabinet: ["Slim Cabinet", "Large Cabinet"],
      Monitor: ["LED Lighting Around the Monitor", "No LED Lighting Around the Monitor"],
      "Additional Hardware": ["Anti-Cheat Device", "Machine Tracker", "Anti-Cheat Device & Machine Tracker"],
      Installation: ["Yes", "No"],
    },
  };
}
// Merchant supplied these options for the published Roulette and Pearl machines.
// Other games retain the enquiry workflow until their specification is confirmed.
export const confirmedProfiles: Record<string, MachineProfile> = {
  "6738bbcb5cafc60b4ed27920": machineProfile("Pearl of the Caribbean"),
  "6738bbcc5cafc60b4ed27922": machineProfile("American Roulette"),
  "6738bbcc5cafc60b4ed27923": machineProfile("American Roulette"),
  "6738bbcc5cafc60b4ed27925": machineProfile("American Roulette"),
};
// New catalogue listings inherit the merchant's approved game-family options.
// An explicit per-machine profile always takes priority; unrelated games remain enquiries.
export function profileForMachine(machine?: { _id?: string; name?: string }): MachineProfile | undefined {
  if (!machine) return undefined;
  const name = machine.name?.trim().replace(/\s+/g, " ") || "";
  const profile = (machine._id && confirmedProfiles[machine._id])
    || (/^Pearl of the Caribbean\b/i.test(name) ? machineProfile("Pearl of the Caribbean")
      : /^American Roulette\b/i.test(name) ? machineProfile("American Roulette") : undefined);
  // A listing sold in one cabinet, e.g. "… Master in Slim Cabinet", offers only that cabinet.
  const cabinet = listingCabinet(name);
  if (!profile || !cabinet) return profile;
  return { ...profile, options: { ...profile.options, Cabinet: [cabinet] } };
}

// The cabinet a listing is sold in, from names like "Scratcher Party in Slim Cabinet".
export function listingCabinet(name?: string): string {
  const cabinet = name?.trim().match(/\bin (Slim|Large) Cabinet$/i)?.[1];
  return cabinet ? `${cabinet[0].toUpperCase()}${cabinet.slice(1).toLowerCase()} Cabinet` : "";
}

// Listings not yet on sale say so in an "Availability" section: "## Availability" then "Coming soon."
export function isComingSoon(machine?: { description?: string }): boolean {
  return /^#{1,6}\s*Availability\s*\n+\s*Coming soon\b/im.test(machine?.description || "");
}

// Machines labelled "New" in the builder, until the given time (UTC; 05:00Z is midnight in Jamaica).
export const newMachines: Record<string, string> = {
  "6aea636bad32027c0da86836": "2027-01-01T05:00:00Z", // Scratcher Party in Slim Cabinet
  "6aea63dcad32027c0da86879": "2027-01-01T05:00:00Z", // Scratcher Party in Large Cabinet
};
export function isNewMachine(id?: string, now = Date.now()): boolean {
  const until = id ? newMachines[id] : undefined;
  return !!until && now < Date.parse(until);
}

// Machine picker order: new machines first, coming-soon ones last, otherwise as listed.
export function pickerOrder<T extends { _id?: string; description?: string }>(machines: T[], now = Date.now()): T[] {
  const rank = (m: T) => (isNewMachine(m._id, now) ? 0 : isComingSoon(m) ? 2 : 1);
  return machines.map((m, i) => ({ m, i })).sort((a, b) => rank(a.m) - rank(b.m) || a.i - b.i).map(({ m }) => m);
}
// Shop products that match the builder's hardware options, so the builder can
// show the real part. "TOPS Printer" is matched to the TOP TGP58 (same maker).
export const partProducts: Record<string, string> = {
  "TOPS Bill Acceptor": "674df3bb43694757801c54a7",
  "Pyramid 7600 Bill Acceptor": "674df30243694757801c53e1",
  "Pyramid Platinum": "6abc8ea835978d0228ff0c71",
  "TOPS Printer": "674df3bc43694757801c54a8",
  "Pyramid Printer": "674df2e343694757801c53c1",
};
export type Installation = {
  name: string;
  parish: string;
  image: string;
  imageAlt: string;
  machines: string;
  count?: number;
  photos?: { src: string; alt: string }[];
  arrangement?: "purchase" | "lease" | "both";
};
// Add only approved locations and photographs; an empty list shows an enquiry instead of fabricated proof.
export const approvedInstallations: Installation[] = [];
export function quoteMessage(build: {
  theme: string;
  selections: Record<string, string>;
  quantity: number;
  intent: string;
  name: string;
  contact: string;
  location: string;
  notes: string;
  confirmed: boolean;
  base?: string[];
  linked?: string;
  link?: string;
}) {
  return [
    "TECHNOGENIOUS GAMING MACHINE QUOTE REQUEST",
    `Game/theme: ${build.theme}`,
    `Interest: ${build.intent}`,
    `Quantity: ${build.quantity}`,
    ...(build.linked ? [`Linked setup: ${build.linked}`] : []),
    `Base configuration: ${build.base?.join("; ") || "To be confirmed"}`,
    build.confirmed
      ? "Hardware selections: subject to final quotation."
      : "Hardware preferences: compatibility and standard configuration require confirmation.",
    ...componentLabels.map(
      (label) => `${label}: ${build.selections[label] || "Please recommend"}`
    ),
    `Name: ${build.name}`,
    `Contact: ${build.contact}`,
    `Location/parish: ${build.location}`,
    `Notes: ${build.notes || "None"}`,
    ...(build.link ? [`Build link: ${build.link}`] : []),
    "Please confirm availability, compatibility, installation, delivery, and quotation. No order has been placed.",
  ].join("\n");
}
